import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { isEmpty, isNil, xor } from 'lodash'
import { IconPencil } from '@tabler/icons-react'

import { apiErrorMessage } from '@/api'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Button } from '@/components/buttons/Button'
import { Checkbox } from '@/components/forms/Checkbox'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { FormField } from '@/components/forms/FormField'
import { TextArea } from '@/components/forms/TextArea'
import { toast } from '@/components/toasts/toast.helpers'

import {
  ANSWER_SAVE_FAILED,
  VERIFIED_ANSWER_MAX_ANSWER_CHARS,
  getCapReason,
} from './AiAnswers.helpers'
import { AiAnswersAnswerHistory } from './AiAnswersAnswerHistory'
import { AiAnswersPlaybookField } from './AiAnswersPlaybookField'
import { updateVerifiedAnswer } from './verifiedAnswers.api'

/**
 * @import import { AiAnswersEditAnswer } from '@/views/AiAnswers/AiAnswersEditAnswer'
 * @param {object} props
 * @param {object} props.verifiedAnswer The saved answer to edit
 * @param {object} props.verifiedAnswers agent-gpt's verified answers list with the org's usage
 * @param {object} props.playbooks The org's playbook options and their load state
 * @param {number} props.organizationId The org the answer belongs to
 * @param {Function} props.onSaved Called after the edit saves
 */
export const AiAnswersEditAnswer = ({
  verifiedAnswer,
  verifiedAnswers,
  playbooks,
  organizationId,
  onSaved,
}) => {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const [answer, setAnswer] = useState('')
  const [configCids, setConfigCids] = useState([])
  const [everyPlaybookConfirmed, setEveryPlaybookConfirmed] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const trimmedAnswer = answer.trim()
  // Widening a limited answer can serve it across clients a playbook kept apart, so clearing
  // the last playbook isn't enough on its own: the manager confirms it.
  const widening = !isEmpty(verifiedAnswer.config_cids) && isEmpty(configCids)
  // Sent only when changed, so a text fix made from a stale list can't undo a scope another
  // manager set since.
  const scopeChanged = !isEmpty(xor(configCids, verifiedAnswer.config_cids ?? []))
  // Every save is recorded in the answer's history, so one that changes nothing isn't offered.
  const answerChanged = trimmedAnswer !== verifiedAnswer.answer
  const capReason = getCapReason(
    verifiedAnswers,
    verifiedAnswer.question.length + trimmedAnswer.length,
    verifiedAnswer
  )

  const handleOpen = () => {
    setAnswer(verifiedAnswer.answer)
    setConfigCids(verifiedAnswer.config_cids ?? [])
    setEveryPlaybookConfirmed(false)
    setSaveError(null)
    setOpen(true)
  }

  const handleSave = async () => {
    setSaving(true)
    setSaveError(null)

    try {
      await dispatch(
        updateVerifiedAnswer({
          organizationId,
          answerId: verifiedAnswer.id,
          answer: trimmedAnswer,
          configCids: scopeChanged ? configCids : undefined,
        })
      )
    } catch (error) {
      setSaving(false)
      setSaveError(error?.detail?.message ?? apiErrorMessage(error, ANSWER_SAVE_FAILED))
      return
    }

    // The refetch that follows unmounts this row, so close before handing off.
    toast.success('Verified answer updated')
    setSaving(false)
    setOpen(false)
    onSaved()
  }

  return (
    <>
      <Button basic small square aria-label="Edit answer" tooltip="Edit" onClick={handleOpen}>
        <IconPencil />
      </Button>
      <BasicModal
        title="Edit answer"
        show={open}
        onClose={() => setOpen(false)}
        size="small"
        scrolling
        data-testid="edit-verified-answer"
        footer={
          <>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              brand
              loading={saving}
              disabled={
                isEmpty(trimmedAnswer) ||
                (!answerChanged && !scopeChanged) ||
                !isNil(capReason) ||
                (widening && !everyPlaybookConfirmed) ||
                saving
              }
              onClick={handleSave}
              data-testid="answer-edit-save"
            >
              Save answer
            </Button>
          </>
        }
      >
        <div className="flex-column medium-gap">
          <FormField label="Question">
            <p>{verifiedAnswer.question}</p>
          </FormField>
          <FormField label="Answer" required>
            <TextArea
              rows={6}
              value={answer}
              maxLength={VERIFIED_ANSWER_MAX_ANSWER_CHARS}
              onChange={(event) => setAnswer(event.target.value)}
              data-testid="answer-edit-answer"
            />
          </FormField>
          <AiAnswersPlaybookField
            playbooks={playbooks}
            value={configCids}
            onChange={(cids) => {
              setConfigCids(cids)
              setEveryPlaybookConfirmed(false)
            }}
          />
          {widening && (
            <>
              <ErrorMessage
                warning
                content="With no playbooks chosen, AgentGPT will use this answer for every playbook."
              />
              <Checkbox
                label="Apply this answer to every playbook"
                checked={everyPlaybookConfirmed}
                onChange={(event, { checked }) => setEveryPlaybookConfirmed(checked)}
              />
            </>
          )}
          {capReason && <ErrorMessage warning content={capReason} />}
          {saveError && <ErrorMessage content={saveError} />}
          <AiAnswersAnswerHistory
            organizationId={organizationId}
            answerId={verifiedAnswer.id}
            playbooks={playbooks}
          />
        </div>
      </BasicModal>
    </>
  )
}
